import React, { useEffect, useRef, useState } from 'react';
import { useStaffNotifications, type StaffNotification } from '../../hooks/useStaffNotifications';
import { useNavigateToPage } from '../../contexts/NavigationContext';

const KIND_STYLES: Record<StaffNotification['kind'], { dot: string; label: string }> = {
  catering: { dot: 'bg-pink-500', label: 'Catering' },
  chat: { dot: 'bg-blue-500', label: 'App chat' },
  feedback: { dot: 'bg-amber-500', label: 'Feedback' },
  meal_plan_exception: { dot: 'bg-green-600', label: 'Meal plans' },
};

function timeAgo(ms: number): string {
  if (!ms) return '';
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{children}</div>
);

const AlertRow: React.FC<{ item: StaffNotification; time: string; read?: boolean; onClick: () => void }> = ({
  item,
  time,
  read = false,
  onClick,
}) => (
  <li>
    <button type="button" className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex gap-3 ${read ? 'opacity-60' : ''}`} onClick={onClick}>
      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${read ? 'bg-gray-300' : KIND_STYLES[item.kind].dot}`} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${read ? 'text-gray-700' : 'font-medium text-gray-900'}`}>{item.title}</span>
          <span className="text-[11px] text-gray-400 shrink-0">{time}</span>
        </span>
        {item.detail && <span className="block text-xs text-gray-600 truncate">{item.detail}</span>}
        <span className="block text-[11px] text-gray-400 mt-0.5">Open {KIND_STYLES[item.kind]?.label ?? 'page'} →</span>
      </span>
    </button>
  </li>
);

/**
 * Bell in the POS header: red count badge when anything needs staff attention (new catering
 * requests, unread app chat, open app feedback, pending meal-plan exceptions — see
 * useStaffNotifications). Clicking an item dismisses it (on every terminal) and opens the page
 * where it's handled; it comes back only if there is new activity (e.g. another chat message).
 * Opened alerts stay listed under "Earlier" (last 4, scroll or "Load more" for older).
 */
const NotificationBell: React.FC = () => {
  const { items, count, readItems, hasMoreRead, loadMoreRead, resetReadPage, markSeen } = useStaffNotifications();
  const navigate = useNavigateToPage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Closing always returns "Earlier" to its first page (last 4).
  const close = () => {
    setOpen(false);
    resetReadPage();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        className={`relative flex items-center gap-1 rounded-full px-2 py-1 text-[10px] sm:text-xs font-semibold border ${
          count > 0 ? 'bg-pink-50 border-pink-300 text-pink-700' : 'bg-gray-50 border-gray-200 text-gray-500'
        }`}
        title={count > 0 ? `${count} notification${count === 1 ? '' : 's'}` : 'No notifications'}
        aria-label={`Notifications (${count})`}
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" aria-hidden="true">
          <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z" />
        </svg>
        <span className="hidden sm:inline">Alerts</span>
        {count > 0 && (
          <span className="absolute -top-1.5 -right-1.5 animate-pulse min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[18px] text-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">Notifications</span>
            <span className="text-xs text-gray-500">{count === 0 ? 'All clear' : `${count} need attention`}</span>
          </div>
          <div
            className="max-h-96 overflow-y-auto"
            onScroll={(e) => {
              // Near the bottom → pull in more read alerts.
              const el = e.currentTarget;
              if (hasMoreRead && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) loadMoreRead();
            }}
          >
            <SectionLabel>New</SectionLabel>
            {items.length === 0 ? (
              <p className="px-4 py-4 text-sm text-gray-500 text-center">Nothing needs attention right now.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((item) => (
                  <AlertRow
                    key={item.id}
                    item={item}
                    time={timeAgo(item.at)}
                    onClick={() => {
                      close();
                      markSeen(item);
                      navigate?.(item.page);
                    }}
                  />
                ))}
              </ul>
            )}

            {readItems.length > 0 && (
              <>
                <SectionLabel>Earlier</SectionLabel>
                <ul className="divide-y divide-gray-100">
                  {readItems.map((item) => (
                    <AlertRow
                      key={`${item.id}-${item.seenAt}`}
                      item={item}
                      time={`opened ${timeAgo(item.seenAt)}`}
                      read
                      onClick={() => {
                        close();
                        navigate?.(item.page);
                      }}
                    />
                  ))}
                </ul>
                {hasMoreRead && (
                  <button
                    type="button"
                    onClick={loadMoreRead}
                    className="w-full py-2 text-xs font-semibold text-pink-600 hover:bg-gray-50 border-t border-gray-100"
                  >
                    Load more
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
